import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import { XREstimatedLight } from 'three/addons/webxr/XREstimatedLight.js';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const curveGeometry = (width, height, bow) => {
    const geometry = new THREE.PlaneGeometry(width, height, 40, 1);
    const position = geometry.attributes.position;
    for (let index = 0; index < position.count; index += 1) {
        const across = position.getX(index) / (width / 2);
        position.setZ(index, bow * (1 - across * across));
    }
    geometry.computeVertexNormals();
    return geometry;
};

const blurFromImage = (image) => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 40;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearFilter;
    return texture;
};

const edgeFadeMap = () => {
    const size = 256;
    const fade = 36;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    const pixels = context.createImageData(size, size);
    for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
            const across = x < fade ? x / fade : x > size - 1 - fade ? (size - 1 - x) / fade : 1;
            const down = y < fade ? y / fade : y > size - 1 - fade ? (size - 1 - y) / fade : 1;
            const alpha = Math.min(across, down);
            const index = (y * size + x) * 4;
            const shade = Math.round(alpha * 255);
            pixels.data[index] = shade;
            pixels.data[index + 1] = shade;
            pixels.data[index + 2] = shade;
            pixels.data[index + 3] = 255;
        }
    }
    context.putImageData(pixels, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.NoColorSpace;
    return texture;
};

const skyColorFrom = (image) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext('2d');
    context.drawImage(image, image.width * 0.5, 2, 8, 8, 0, 0, 1, 1);
    const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
    return new THREE.Color(red / 255, green / 255, blue / 255);
};

const sampleColor = (image, across, down) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext('2d');
    context.drawImage(
        image,
        image.width * across,
        image.height * down,
        16,
        16,
        0,
        0,
        1,
        1
    );
    const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
    return new THREE.Color(red / 255, green / 255, blue / 255);
};

const makePavement = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const context = canvas.getContext('2d');
    context.fillStyle = '#c9c2b4';
    context.fillRect(0, 0, 256, 256);
    context.strokeStyle = 'rgba(80, 74, 64, 0.28)';
    context.lineWidth = 2;
    for (let line = 0; line <= 256; line += 64) {
        context.beginPath();
        context.moveTo(line, 0);
        context.lineTo(line, 256);
        context.stroke();
        context.beginPath();
        context.moveTo(0, line);
        context.lineTo(256, line);
        context.stroke();
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(24, 24);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
};

const makeVrSky = (horizon) => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 256;
    const context = canvas.getContext('2d');
    const gradient = context.createLinearGradient(0, 0, 0, 256);
    gradient.addColorStop(0, '#7eb4e4');
    gradient.addColorStop(0.46, '#d5e7f6');
    gradient.addColorStop(0.62, `#${horizon.getHexString()}`);
    gradient.addColorStop(1, '#c8c0b2');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 32, 256);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
};

const ServiceViewer = ({ image, title, tagline, onClose }) => {
    const mountRef = useRef(null);
    const overlayRef = useRef(null);
    const sessionApi = useRef(null);
    const onCloseRef = useRef(onClose);
    const hintRef = useRef(() => {});
    onCloseRef.current = onClose;
    const [modes, setModes] = useState({ ar: false, vr: false });
    const [tiltReady, setTiltReady] = useState(false);
    const [xrError, setXrError] = useState('');
    const [xrHint, setXrHint] = useState('');
    hintRef.current = setXrHint;

    useEffect(() => {
        let alive = true;
        if (!navigator.xr) return undefined;
        Promise.all([
            navigator.xr.isSessionSupported('immersive-ar').catch(() => false),
            navigator.xr.isSessionSupported('immersive-vr').catch(() => false),
        ]).then(([ar, vr]) => {
            if (alive) setModes({ ar, vr });
        });
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return undefined;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const look = {
            yaw: 0,
            pitch: 0.04,
            targetYaw: 0,
            targetPitch: 0.04,
            drag: false,
            lastX: 0,
            lastY: 0,
        };

        const skyColor = new THREE.Color(0x8ea4b5);
        const scene = new THREE.Scene();
        scene.background = skyColor;
        const camera = new THREE.PerspectiveCamera(52, 1, 0.05, 80);
        camera.rotation.order = 'YXZ';

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setClearColor(0x000000, 0);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.xr.enabled = true;
        mount.appendChild(renderer.domElement);

        const viewRoot = new THREE.Group();
        scene.add(viewRoot);
        const site = new THREE.Group();
        site.visible = false;
        scene.add(site);
        const world = new THREE.Group();
        site.add(world);
        const reticle = new THREE.Mesh(
            new THREE.RingGeometry(0.14, 0.18, 32).rotateX(-Math.PI / 2),
            new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95 })
        );
        reticle.matrixAutoUpdate = false;
        reticle.visible = false;
        scene.add(reticle);

        const disposables = [];
        let sharp = null;
        let haze = null;
        let facade = null;
        let shadowCatcher = null;
        let facadeHeight = 2.6;
        let imageAspect = 1.6;
        let markReady = () => {};
        const facadeReady = new Promise((resolve) => {
            markReady = resolve;
        });
        const standDistance = 6.4;

        const layout = () => {
            const vertical = THREE.MathUtils.degToRad(camera.fov);
            const visibleH = 2 * standDistance * Math.tan(vertical / 2);
            const visibleW = visibleH * camera.aspect;
            const margin = 1.72;
            let height = visibleH * margin;
            let width = height * imageAspect;
            if (width < visibleW * margin) {
                width = visibleW * margin;
                height = width / imageAspect;
            }
            return { width, height };
        };

        const rebuild = () => {
            if (!sharp || !haze) return;
            const { width, height } = layout();
            sharp.geometry.dispose();
            haze.geometry.dispose();
            sharp.geometry = curveGeometry(width, height, width * 0.02);
            haze.geometry = curveGeometry(width * 1.7, height * 1.7, width * 0.03);
            sharp.position.set(0, height * 0.02, -standDistance);
            haze.position.set(0, height * 0.02, -standDistance - 0.85);
        };

        const placeForScreen = () => {
            viewRoot.visible = true;
            viewRoot.position.set(0, 0, 0);
            viewRoot.scale.set(1, 1, 1);
            site.visible = false;
            scene.background = skyColor;
            scene.environment = null;
            if (haze) haze.visible = true;
            hintRef.current('');
        };

        const texture = new THREE.TextureLoader().load(image, (loaded) => {
            loaded.colorSpace = THREE.SRGBColorSpace;
            loaded.anisotropy = renderer.capabilities.getMaxAnisotropy();
            imageAspect = loaded.image.width / loaded.image.height;
            skyColor.copy(skyColorFrom(loaded.image));
            scene.background = skyColor;
            const hazeTex = blurFromImage(loaded.image);
            const fadeTex = edgeFadeMap();
            const { width, height } = layout();

            const sharpMat = new THREE.MeshBasicMaterial({
                map: loaded,
                alphaMap: fadeTex,
                transparent: true,
                toneMapped: false,
            });
            const hazeMat = new THREE.MeshBasicMaterial({
                map: hazeTex,
                toneMapped: false,
            });
            disposables.push(sharpMat, hazeMat, hazeTex, fadeTex);

            haze = new THREE.Mesh(curveGeometry(width * 1.7, height * 1.7, width * 0.03), hazeMat);
            sharp = new THREE.Mesh(curveGeometry(width, height, width * 0.02), sharpMat);
            sharp.position.set(0, height * 0.02, -standDistance);
            haze.position.set(0, height * 0.02, -standDistance - 0.85);
            viewRoot.add(haze, sharp);
            disposables.push(sharp.geometry, haze.geometry);

            const aspect = loaded.image.width / loaded.image.height;
            facadeHeight = 2.6;
            let facadeWidth = facadeHeight * aspect;
            if (facadeWidth > 3.6) {
                facadeWidth = 3.6;
                facadeHeight = facadeWidth / aspect;
            }
            if (facadeHeight > 3.2) {
                facadeHeight = 3.2;
                facadeWidth = facadeHeight * aspect;
            }
            const depth = clamp(facadeWidth * 0.14, 0.28, 0.62);
            const sideColor = sampleColor(loaded.image, 0.08, 0.45).multiplyScalar(0.72);
            const stone = new THREE.MeshStandardMaterial({ color: sideColor, roughness: 0.78, metalness: 0.06 });
            const metal = new THREE.MeshStandardMaterial({ color: 0xe6edf2, metalness: 0.92, roughness: 0.22 });
            const plinthMat = new THREE.MeshStandardMaterial({ color: 0x8a847c, roughness: 0.9 });
            const photoMat = new THREE.MeshBasicMaterial({ map: loaded, toneMapped: false });
            disposables.push(stone, metal, plinthMat, photoMat);

            facade = new THREE.Group();
            const plinth = new THREE.Mesh(
                new THREE.BoxGeometry(facadeWidth + 0.12, 0.08, depth + 0.16),
                plinthMat
            );
            plinth.position.set(0, 0.04, -depth / 2);
            plinth.castShadow = true;
            plinth.receiveShadow = true;
            facade.add(plinth);

            const body = new THREE.Mesh(new THREE.BoxGeometry(facadeWidth, facadeHeight, depth), stone);
            body.position.set(0, facadeHeight / 2, -depth / 2);
            body.castShadow = true;
            body.receiveShadow = true;
            facade.add(body);

            const photo = new THREE.Mesh(new THREE.PlaneGeometry(facadeWidth - 0.04, facadeHeight - 0.04), photoMat);
            photo.position.set(0, facadeHeight / 2, 0.012);
            facade.add(photo);

            const midY = facadeHeight / 2;
            const bars = [
                [facadeWidth + 0.06, 0.045, 0.045, 0, facadeHeight - 0.02, 0.03],
                [facadeWidth + 0.06, 0.045, 0.045, 0, 0.08, 0.03],
                [0.045, facadeHeight, 0.045, -facadeWidth / 2, midY, 0.03],
                [0.045, facadeHeight, 0.045, facadeWidth / 2, midY, 0.03],
            ];
            bars.forEach(([w, h, d, x, y, z]) => {
                const bar = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), metal);
                bar.position.set(x, y, z);
                bar.castShadow = true;
                facade.add(bar);
            });

            shadowCatcher = new THREE.Mesh(
                new THREE.PlaneGeometry(6, 6),
                new THREE.ShadowMaterial({ opacity: 0.42 })
            );
            shadowCatcher.rotation.x = -Math.PI / 2;
            shadowCatcher.position.y = 0.008;
            shadowCatcher.receiveShadow = true;
            facade.add(shadowCatcher);
            disposables.push(shadowCatcher.geometry, shadowCatcher.material);

            const arSun = new THREE.DirectionalLight(0xfff6ea, 1.15);
            arSun.position.set(1.4, 4.2, 2.2);
            arSun.castShadow = true;
            arSun.shadow.mapSize.set(1024, 1024);
            arSun.shadow.camera.near = 0.2;
            arSun.shadow.camera.far = 10;
            arSun.shadow.camera.left = -3;
            arSun.shadow.camera.right = 3;
            arSun.shadow.camera.top = 3;
            arSun.shadow.camera.bottom = -3;
            arSun.shadow.bias = -0.001;
            facade.add(arSun);
            facade.add(arSun.target);
            arSun.target.position.set(0, facadeHeight * 0.4, 0);

            site.add(facade);

            const pavement = makePavement();
            const ground = new THREE.Mesh(
                new THREE.CircleGeometry(40, 48),
                new THREE.MeshStandardMaterial({ map: pavement, roughness: 0.92, metalness: 0.02 })
            );
            ground.rotation.x = -Math.PI / 2;
            ground.receiveShadow = true;
            world.add(ground);
            disposables.push(pavement, ground.geometry, ground.material);

            const skyTex = makeVrSky(skyColor);
            const sky = new THREE.Mesh(
                new THREE.SphereGeometry(46, 24, 16),
                new THREE.MeshBasicMaterial({ map: skyTex, side: THREE.BackSide, depthWrite: false })
            );
            world.add(sky);
            disposables.push(skyTex, sky.geometry, sky.material);

            const hemi = new THREE.HemisphereLight(0xd7ecff, 0xc4bba8, 0.7);
            const sun = new THREE.DirectionalLight(0xfff3e2, 1.35);
            sun.position.set(-8, 16, 6);
            sun.castShadow = true;
            sun.shadow.mapSize.set(2048, 2048);
            sun.shadow.camera.near = 2;
            sun.shadow.camera.far = 40;
            sun.shadow.camera.left = -16;
            sun.shadow.camera.right = 16;
            sun.shadow.camera.top = 16;
            sun.shadow.camera.bottom = -16;
            sun.shadow.bias = -0.0004;
            world.add(hemi, sun);
            markReady();
        }, undefined, () => markReady());

        const applyLook = (time) => {
            const sway = reducedMotion || look.drag ? 0 : Math.sin(time * 0.45) * 0.012;
            const yaw = look.yaw + sway;
            const { pitch } = look;
            camera.position.set(-yaw * 0.42, pitch * 0.12, 0);
            camera.rotation.y = yaw * 0.72;
            camera.rotation.x = -pitch;
        };

        const onMove = (event) => {
            if (!look.drag || renderer.xr.isPresenting) return;
            const dx = event.clientX - look.lastX;
            const dy = event.clientY - look.lastY;
            look.targetYaw = clamp(look.targetYaw - dx * 0.0026, -0.36, 0.36);
            look.targetPitch = clamp(look.targetPitch + dy * 0.0018, -0.2, 0.26);
            look.lastX = event.clientX;
            look.lastY = event.clientY;
        };
        const onDown = (event) => {
            if (event.target.closest('button')) return;
            look.drag = true;
            look.lastX = event.clientX;
            look.lastY = event.clientY;
        };
        const onUp = () => {
            look.drag = false;
        };
        const onOrient = (event) => {
            if (renderer.xr.isPresenting) return;
            look.targetYaw = clamp((event.gamma || 0) / 55, -0.36, 0.36);
            look.targetPitch = clamp(((event.beta || 55) - 68) / 110, -0.2, 0.26);
        };

        mount.addEventListener('pointerdown', onDown);
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);

        const resize = () => {
            const viewWidth = mount.clientWidth;
            const viewHeight = mount.clientHeight;
            if (!viewWidth || !viewHeight) return;
            camera.aspect = viewWidth / viewHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(viewWidth, viewHeight);
            rebuild();
        };
        resize();
        window.addEventListener('resize', resize);

        const clock = new THREE.Clock();
        let frameId = 0;
        const renderScreen = () => {
            frameId = window.requestAnimationFrame(renderScreen);
            if (renderer.xr.isPresenting) return;
            look.yaw += (look.targetYaw - look.yaw) * 0.09;
            look.pitch += (look.targetPitch - look.pitch) * 0.09;
            applyLook(clock.getElapsedTime());
            renderer.render(scene, camera);
        };
        renderScreen();

        sessionApi.current = {
            enableTilt: async () => {
                const orientation = window.DeviceOrientationEvent;
                if (orientation && typeof orientation.requestPermission === 'function') {
                    const result = await orientation.requestPermission();
                    if (result !== 'granted') return false;
                }
                window.addEventListener('deviceorientation', onOrient);
                return true;
            },
            enter: async (mode) => {
                await facadeReady;
                if (!facade) throw new Error('The facade is still loading.');

                const overlay = overlayRef.current;
                let session;
                try {
                    session = await navigator.xr.requestSession(mode, {
                        optionalFeatures: ['local-floor', 'hit-test', 'light-estimation', 'dom-overlay'],
                        ...(mode === 'immersive-ar' && overlay ? { domOverlay: { root: overlay } } : {}),
                    });
                } catch {
                    session = await navigator.xr.requestSession(mode, {
                        optionalFeatures: ['local-floor', 'hit-test', 'light-estimation'],
                    });
                }

                viewRoot.visible = false;
                site.visible = true;

                const faceUser = () => {
                    const eyes = new THREE.Vector3();
                    renderer.xr.getCamera().getWorldPosition(eyes);
                    facade.lookAt(eyes.x, facade.position.y, eyes.z);
                    facade.rotateY(Math.PI);
                };

                let hitSource = null;
                let placed = mode !== 'immersive-ar';
                let xrLight = null;

                if (mode === 'immersive-ar') {
                    world.visible = false;
                    if (shadowCatcher) shadowCatcher.visible = true;
                    facade.visible = false;
                    facade.scale.set(1, 1, 1);
                    facade.position.set(0, 0, -2.1);
                    facade.rotation.set(0, 0, 0);
                    facade.traverse((node) => {
                        if (node.isDirectionalLight) node.visible = true;
                    });
                    scene.background = null;
                    reticle.visible = false;
                    hintRef.current('Point at the floor and tap to place the facade.');
                    xrLight = new XREstimatedLight(renderer);
                    xrLight.directionalLight.castShadow = true;
                    scene.add(xrLight);
                    xrLight.addEventListener('estimationstart', () => {
                        if (xrLight.environment) scene.environment = xrLight.environment;
                    });
                } else {
                    world.visible = true;
                    if (shadowCatcher) shadowCatcher.visible = false;
                    reticle.visible = false;
                    facade.visible = true;
                    facade.scale.setScalar(9.5 / facadeHeight);
                    facade.position.set(0, 0, -11);
                    facade.rotation.set(0, 0, 0);
                    facade.traverse((node) => {
                        if (node.isDirectionalLight) node.visible = false;
                    });
                    scene.background = skyColor;
                    hintRef.current('You are standing in front of the building. Look up and walk toward it.');
                }

                const placeFromReticle = () => {
                    if (!reticle.visible) return;
                    facade.position.setFromMatrixPosition(reticle.matrix);
                    facade.scale.set(1, 1, 1);
                    facade.rotation.set(0, 0, 0);
                    facade.visible = true;
                    faceUser();
                    placed = true;
                    reticle.visible = false;
                    hintRef.current('Walk around the facade. It is standing on the floor where you tapped.');
                };

                const onSelect = () => {
                    if (mode === 'immersive-ar') placeFromReticle();
                };

                const finish = () => {
                    renderer.setAnimationLoop(null);
                    hitSource?.cancel();
                    session.removeEventListener('select', onSelect);
                    session.removeEventListener('end', finish);
                    reticle.visible = false;
                    if (xrLight) {
                        scene.environment = null;
                        scene.remove(xrLight);
                        xrLight.dispose();
                        xrLight = null;
                    }
                    placeForScreen();
                    resize();
                };

                session.addEventListener('select', onSelect);
                session.addEventListener('end', finish);
                try {
                    renderer.xr.setReferenceSpaceType('local-floor');
                    await renderer.xr.setSession(session);
                } catch {
                    try {
                        renderer.xr.setReferenceSpaceType('local');
                        await renderer.xr.setSession(session);
                    } catch (error) {
                        finish();
                        throw error;
                    }
                }

                if (mode === 'immersive-ar') {
                    try {
                        const viewerSpace = await session.requestReferenceSpace('viewer');
                        hitSource = await session.requestHitTestSource({ space: viewerSpace });
                    } catch {
                        facade.visible = true;
                        faceUser();
                        placed = true;
                        hintRef.current('The facade is on the floor in front of you. Walk around it.');
                    }
                }

                renderer.setAnimationLoop((time, frame) => {
                    if (mode === 'immersive-ar' && frame && hitSource && !placed) {
                        const reference = renderer.xr.getReferenceSpace();
                        const hits = reference ? frame.getHitTestResults(hitSource) : [];
                        if (hits.length && reference) {
                            const pose = hits[0].getPose(reference);
                            if (pose) {
                                reticle.visible = true;
                                reticle.matrix.fromArray(pose.transform.matrix);
                            }
                        } else {
                            reticle.visible = false;
                        }
                    }
                    renderer.render(scene, camera);
                });
            },
        };

        const onKey = (event) => {
            if (event.key === 'Escape') onCloseRef.current();
        };
        window.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            window.cancelAnimationFrame(frameId);
            renderer.setAnimationLoop(null);
            window.removeEventListener('resize', resize);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('deviceorientation', onOrient);
            mount.removeEventListener('pointerdown', onDown);
            document.body.style.overflow = previousOverflow;
            sessionApi.current = null;
            if (renderer.xr.isPresenting) renderer.xr.getSession()?.end();
            texture.dispose();
            disposables.forEach((item) => item.dispose());
            site.traverse((node) => {
                if (node.geometry) node.geometry.dispose();
            });
            reticle.geometry.dispose();
            reticle.material.dispose();
            renderer.dispose();
            if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
        };
    }, [image]);

    const enableTilt = async () => {
        const granted = await sessionApi.current?.enableTilt();
        if (granted) setTiltReady(true);
    };

    const enter = async (mode) => {
        setXrError('');
        try {
            await sessionApi.current?.enter(mode);
        } catch {
            setXrError('This device could not open that view. Drag to look around instead.');
        }
    };

    return createPortal(
        <div className="xr-viewer" role="dialog" aria-modal="true" aria-label={`${title} view`}>
            <div ref={mountRef} className="xr-viewer-stage" />
            <div ref={overlayRef} className="xr-viewer-copy">
                <button type="button" className="xr-close" onClick={onClose}>Close</button>
                <p className="eyebrow">{title}</p>
                <h2>{tagline}</h2>
                <p>
                    {xrHint || 'Drag to look around the site. On a phone, tilt the screen.'}
                </p>
                {!xrHint && (modes.ar || modes.vr) && (
                    <p>
                        {modes.ar ? 'In AR, point at the floor and tap to place the facade. ' : ''}
                        {modes.vr ? 'In VR, you stand in front of the building.' : ''}
                    </p>
                )}
                {xrError && <p>{xrError}</p>}
                <div className="xr-viewer-actions">
                    {!tiltReady && (
                        <button type="button" className="hero-btn" onClick={enableTilt}>Tilt to look</button>
                    )}
                    {modes.ar && (
                        <button type="button" className="hero-btn is-solid" onClick={() => enter('immersive-ar')}>View in AR</button>
                    )}
                    {modes.vr && (
                        <button type="button" className="hero-btn" onClick={() => enter('immersive-vr')}>View in VR</button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ServiceViewer;
